import React, {useEffect, useState} from "react";
import {useAuth} from "context/AuthContext";
import {useLocation, useNavigate} from "react-router-dom";
import LocationState from "types/LocationState";
import {useOperator} from "context/OperatorContext";
import Logo from "components/Logo";
import {useMutation} from "react-query";
import Alert from "components/Alert";
import AuthCredentials from "../../types/AuthCredentials";
import {LoginForm} from "./Form/LoginForm";
import {Transition} from "@headlessui/react";

function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const {operator, refetchOperator} = useOperator();
    const auth = useAuth();

    const operatorLogo = operator?.settings.branding.logo;

    const {mutateAsync: login, data: result, isLoading, reset: clearResult} = useMutation(auth.login);

    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [loginComplete, setLoginComplete] = useState(false);

    const state = location.state as LocationState
    const from = state?.from?.pathname || '/';

    useEffect(() => {
        if (loginComplete && auth.user) {
            navigate(from, {replace: true});
        }
    }, [auth.user, from, loginComplete, navigate]);

    const handleChange = () => {
        if (!isLoading && result) {
            clearResult();
            setAlertMessage(null)
        }
    }

    const handleSubmit = async (credentials: AuthCredentials) => {
        const result = await login(credentials);

        switch(result.status) {
            case 'failed':
                setAlertMessage(result.message || "Login failed");
                break;
            case 'success':
                // The user doesn't have permission to access this app
                if (!result.operator_access) {
                    auth.clearUser();
                    setAlertMessage(`You do not have permission to access this app. Please contact ${operator?.name || 'the operator'} for more information.`);
                    return;
                }

                // If the user has two factor auth enabled, they need to complete the challenge before we consider them logged in
                if (result.two_factor) {
                    return navigate('/two-factor', {state});
                } else {
                    // Navigation is handled by the effect once the auth context exposes the refetched user.
                    await auth.refetchCurrentUser();
                    await refetchOperator();
                    setLoginComplete(true);
                }

                break;
            default:
                setAlertMessage("An unexpected error occurred. Please try again.");
        }
    }

    return (
      <Transition
        show={true}
        appear={true}
        enter="transition-all duration-1000"
        enterFrom="opacity-0 translate-y-5"
        enterTo="opacity-100 translate-x-0"
        className='w-full max-w-[22rem] md:min-w-[26.5rem] md:max-w-[26.5rem] flex flex-col gap-6'
      >
        <div className="max-w-[8rem] pb-8 md:hidden">
            <Logo src={operatorLogo?.original_url} size={Logo.sizes.X_LARGE} />
        </div>

        <div className='flex flex-col gap-1.5 pb-2'>
            <h1 className="text-2xl font-medium">Turn every visit into opportunity</h1>
            <p className='text-lg text-gray-500'>Sign in to {operator?.name}</p>
        </div>

        {alertMessage && <Alert type={Alert.types.ERROR} message={alertMessage}/>}

        <LoginForm onSubmit={handleSubmit} onChange={handleChange} />
      </Transition>
    )
}

export { Login };